import React, { useEffect, useState, useCallback } from 'react';
import {
  Edit2, Trash2, Plus, Loader, AlertCircle, Check, CreditCard,
  Smartphone, MessageCircle, X, Shield, Zap, Activity,
  ChevronRight, ChevronDown, Hash, Globe, Cpu, ArrowUpRight, Award,
  Package, TrendingUp, Users, PieChart, FileText, CheckCircle,
  BarChart, Layers, Info, Trash, RefreshCw, Tag, Save
} from 'lucide-react';
import api from '../../services/api';
import PageHeader from '../../components/PageHeader';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import { formatCurrency, getCurrencySymbol } from '../../utils/currency';
import PlanFeatureGroups, { groupKeysByCatalog } from '../../components/PlanFeatureGroups';

// Cross-tenant aggregate view — platform billing is always reported in the
// platform's own currency, not any individual tenant's setting.
const PLATFORM_CURRENCY = 'INR';

// Fallback only — used if GET /admin/feature-catalog hasn't loaded yet or
// fails. The real, authoritative list (src/config/featureCatalog.js, ~78
// per-page keys) is fetched at mount and rendered as the grouped picker
// below; this covers the handful of legacy keys that predate that catalog.
const FALLBACK_FEATURES = {
  'Legacy': [
    { key: 'agentic_support_widget', label: 'Agentic Support Widget (legacy — superseded by the AI & Support group below)' },
  ],
};

function PlanModal({ plan, catalogGroups, onClose, onSaved }) {
  const isEdit = !!plan;
  const [form, setForm] = useState(
    isEdit
      ? {
        name: plan.name || '',
        price: plan.price || '',
        billingCycle: plan.billingCycle || 'monthly',
        description: plan.description || '',
        maxDevices: plan.maxDevices || 1,
        // null means unlimited on the model — kept as '' in the form so the
        // input can render empty rather than the string "null".
        maxUsers: plan.maxUsers ?? '',
        monthlyMessageLimit: plan.monthlyMessageLimit || 1000,
        messagesPerMinute: plan.messagesPerMinute || 20,
        features: plan.features || [],
        isActive: plan.isActive !== false,
        planType: plan.planType || 'paid',
        trialDurationDays: plan.trialDurationDays || 7,
      }
      : {
        name: '',
        price: '',
        billingCycle: 'monthly',
        description: '',
        maxDevices: 1,
        maxUsers: '',
        monthlyMessageLimit: 1000,
        messagesPerMinute: 20,
        features: [],
        isActive: true,
        planType: 'paid',
        trialDurationDays: 7,
      }
  );
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const groups = Object.keys(catalogGroups).length ? catalogGroups : FALLBACK_FEATURES;

  const toggleGroupCollapsed = (groupName) => {
    setCollapsedGroups(c => ({ ...c, [groupName]: !c[groupName] }));
  };

  const toggleWholeGroup = (groupName, keys) => {
    const allSelected = keys.every(k => form.features.includes(k));
    setForm(f => ({
      ...f,
      features: allSelected
        ? f.features.filter(x => !keys.includes(x))
        : [...new Set([...f.features, ...keys])],
    }));
  };

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    if (errors[k]) setErrors(prev => ({ ...prev, [k]: null }));
  };

  // Switching to Trial clears every "AI & Support" toggle and zeroes the
  // price — a trial plan is meant to be free and AI-free by construction,
  // not just by convention the admin has to remember every time.
  const aiFeatureKeys = [
    'agentic_support_widget',
    ...(groups['AI & Support'] || []).map(f => f.key),
  ];
  const setPlanType = (type) => {
    setForm(f => ({
      ...f,
      planType: type,
      ...(type === 'trial' ? { price: 0, features: f.features.filter(x => !aiFeatureKeys.includes(x)) } : {}),
    }));
  };

  const toggleFeature = (feat) => {
    setForm(f => ({
      ...f,
      features: f.features.includes(feat)
        ? f.features.filter(x => x !== feat)
        : [...f.features, feat],
    }));
  };

  const validate = () => {
    const e = {};
    if (!form.name) e.name = 'Plan title is required';
    if (form.price === '') e.price = 'Rate amount is required';
    else if (Number(form.price) < 0) e.price = 'Rate amount cannot be negative';
    if (form.maxDevices !== '' && Number(form.maxDevices) < 1) e.maxDevices = 'Must allow at least 1 device';
    if (form.maxUsers !== '' && Number(form.maxUsers) < 1) e.maxUsers = 'Must allow at least 1 seat, or leave blank for unlimited';
    if (form.monthlyMessageLimit !== '' && Number(form.monthlyMessageLimit) < 1000) e.monthlyMessageLimit = 'Minimum message limit is 1,000';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    try {
      setSaving(true);
      // '' in the form means "unlimited seats" — the model stores that as
      // null, not the string '' or NaN.
      const payload = { ...form, maxUsers: form.maxUsers === '' ? null : Number(form.maxUsers) };
      if (isEdit) {
        await api.put(`/admin/plans/${plan._id}`, payload);
        alertService.toast('success', 'Plan configuration updated');
      } else {
        await api.post('/admin/plans', payload);
        alertService.toast('success', 'New tier initialized');
      }
      onSaved();
    } catch (e) {
      alertService.error('Update Failed', e.response?.data?.error || 'Failed to save tier parameters.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4 bg-black/40" onClick={onClose}>
      <Card className="w-full max-w-2xl p-0 overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.08)] border-[#E5E5E5] bg-white rounded-t-lg sm:rounded-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-4 sm:px-6 py-4 border-b border-[#E5E5E5] flex items-center justify-between bg-white">
           <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-md bg-[#F4F6F9] border border-[#D8DDE6] flex items-center justify-center text-[#0176D3]">
                 <Package size={20} strokeWidth={2} />
              </div>
              <div className="space-y-1">
                 <h2 className="text-[16px] font-bold text-[#2E2E2E]">{isEdit ? 'Manage Tier Parameters' : 'Create New Plan'}</h2>
                 <p className="text-[13px] text-[#706E6B]">{isEdit ? `Modifying: ${plan.name}` : 'Configure new billing and limit parameters'}</p>
              </div>
           </div>
           <button className="text-[#706E6B] hover:text-[#080707] hover:bg-[#F3F2F2] p-2 rounded-md transition-colors" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
               <label className="text-[12px] font-semibold text-[#444444]">Plan Title</label>
               <Input 
                 icon={Tag}
                 value={form.name} 
                 placeholder="e.g. Starter Tier" 
                 onChange={e => set('name', e.target.value)} 
                 error={errors.name}
                 required 
               />
            </div>
            <div className="space-y-2">
               <label className="text-[12px] font-semibold text-[#444444]">Plan Type</label>
               <div className="flex rounded-md border border-[#D8DDE6] overflow-hidden">
                 <button
                   type="button"
                   onClick={() => setPlanType('paid')}
                   className={cn("flex-1 py-2 text-[12px] font-bold transition-all", form.planType !== 'trial' ? "bg-[#0176D3] text-white" : "bg-white text-[#706E6B] hover:bg-[#F4F6F9]")}
                 >
                   Paid
                 </button>
                 <button
                   type="button"
                   onClick={() => setPlanType('trial')}
                   className={cn("flex-1 py-2 text-[12px] font-bold transition-all", form.planType === 'trial' ? "bg-[#0176D3] text-white" : "bg-white text-[#706E6B] hover:bg-[#F4F6F9]")}
                 >
                   Trial
                 </button>
               </div>
            </div>
          </div>

          {form.planType === 'trial' && (
            <div className="p-3 rounded-md bg-[#EAF4FE] border border-[#0176D3]/20 text-[12px] text-[#0176D3] font-medium">
              Trial plans are free and never appear on the pricing page — a customer only ever gets one by it being set as the
              default trial (see below). AI features (Agentic Mode, Agentic Support Widget, Voice AI) were cleared automatically.
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {form.planType === 'trial' ? (
              <div className="space-y-2">
                 <label className="text-[12px] font-semibold text-[#444444]">Trial Duration (days)</label>
                 <Input
                   icon={RefreshCw}
                   type="number"
                   min="1"
                   value={form.trialDurationDays}
                   onChange={e => set('trialDurationDays', Number(e.target.value))}
                   hint="e.g. 7 for a 7-day trial"
                   required
                 />
              </div>
            ) : (
              <div className="space-y-2">
                 <label className="text-[12px] font-semibold text-[#444444]">Billing Cycle</label>
                 <Select
                   icon={RefreshCw}
                   value={form.billingCycle}
                   onChange={e => set('billingCycle', e.target.value)}
                 >
                   <option value="monthly">Monthly</option>
                   <option value="yearly">Yearly</option>
                 </Select>
              </div>
            )}
            <div className="space-y-2">
               <label className="text-[12px] font-semibold text-[#444444]">Rate Amount ({getCurrencySymbol(PLATFORM_CURRENCY)})</label>
               <Input
                 icon={CreditCard}
                 type="number"
                 min="0"
                 step="0.01"
                 value={form.price}
                 onChange={e => set('price', e.target.value)}
                 error={errors.price}
                 disabled={form.planType === 'trial'}
                 required
               />
            </div>
            <div className="space-y-2">
               <label className="text-[12px] font-semibold text-[#444444]">Device Allotment</label>
               <Input
                 icon={Smartphone}
                 type="number"
                 min="1"
                 value={form.maxDevices}
                 onChange={e => set('maxDevices', Number(e.target.value))}
                 error={errors.maxDevices}
                 required
               />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
               <label className="text-[12px] font-semibold text-[#444444]">Monthly Message Limit</label>
               <Input
                 icon={MessageCircle}
                 type="number"
                 min="1"
                 value={form.monthlyMessageLimit}
                 onChange={e => set('monthlyMessageLimit', Number(e.target.value))}
                 hint="Use 999999 for unlimited monthly volume"
                 error={errors.monthlyMessageLimit}
                 required
               />
            </div>
            <div className="space-y-2">
               <label className="text-[12px] font-semibold text-[#444444]">Messages / Minute</label>
               <Input
                 icon={Zap}
                 type="number"
                 min="1"
                 value={form.messagesPerMinute}
                 onChange={e => set('messagesPerMinute', Number(e.target.value))}
                 hint="Rate limit per company"
               />
            </div>
          </div>

          <div className="space-y-2">
             <label className="text-[12px] font-semibold text-[#444444]">Seat Limit (Team Members)</label>
             <Input
               icon={Users}
               type="number"
               min="1"
               value={form.maxUsers}
               onChange={e => set('maxUsers', e.target.value === '' ? '' : Number(e.target.value))}
               hint="Leave blank for unlimited seats"
               error={errors.maxUsers}
             />
          </div>

          <div className="space-y-2">
            <label className="text-[12px] font-semibold text-[#444444]">Tier Insight</label>
            <textarea 
              className="w-full bg-white border border-[#D8DDE6] rounded p-3 text-[13px] text-[#080707] focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] min-h-[100px] transition-all placeholder:text-[#706E6B] outline-none" 
              rows={2} 
              value={form.description} 
              placeholder="Provide a brief overview of tier entitlements…" 
              onChange={e => set('description', e.target.value)} 
            />
          </div>

          <div className="space-y-3">
            <label className="text-[12px] font-semibold text-[#444444]">Module & Page Entitlements</label>
            <div className="space-y-2">
              {Object.entries(groups).map(([groupName, items]) => {
                const keys = items.map(f => f.key);
                const selectedCount = keys.filter(k => form.features.includes(k)).length;
                const allSelected = selectedCount === keys.length;
                const isCollapsed = collapsedGroups[groupName];
                return (
                  <div key={groupName} className="border border-[#D8DDE6] rounded-md overflow-hidden">
                    <div className="flex items-center justify-between p-3 bg-[#F4F6F9]">
                      <button
                        type="button"
                        onClick={() => toggleGroupCollapsed(groupName)}
                        className="flex items-center gap-2 text-[12px] font-bold text-[#080707]"
                      >
                        {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                        {groupName}
                        <span className="text-[11px] font-normal text-[#706E6B]">({selectedCount}/{keys.length})</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleWholeGroup(groupName, keys)}
                        className="text-[11px] font-bold text-[#0176D3] hover:underline"
                      >
                        {allSelected ? 'Deselect all' : 'Select all'}
                      </button>
                    </div>
                    {!isCollapsed && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3">
                        {items.map(feat => (
                          <label
                            key={feat.key}
                            className={cn(
 "flex items-center gap-2 p-2 rounded-md border transition-all cursor-pointer",
 form.features.includes(feat.key) ? "bg-[#0176D3] border-[#0176D3] text-white" : "bg-white border-[#D8DDE6] text-[#080707] hover:bg-[#F4F6F9]"
 )}
                          >
                            <input
                              type="checkbox"
                              className="hidden"
                              checked={form.features.includes(feat.key)}
                              onChange={() => toggleFeature(feat.key)}
                            />
                            <div className={cn(
 "w-4 h-4 shrink-0 rounded-sm border flex items-center justify-center transition-all",
 form.features.includes(feat.key) ? "border-white" : "border-[#D8DDE6]"
 )}>
                              {form.features.includes(feat.key) && <Check size={12} strokeWidth={3} className="text-white" />}
                            </div>
                            <span className="text-[12px] font-semibold truncate">{feat.label}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <label className="flex items-center justify-between p-4 rounded-lg bg-white border border-[#E5E5E5] hover:bg-[#F4F6F9] transition-all cursor-pointer group shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
            <div className="space-y-1">
               <span className="block text-[13px] font-bold text-[#080707] leading-none">Public Visibility</span>
               <span className="block text-[12px] text-[#706E6B]">Enable or disable this tier for public subscription</span>
            </div>
            <div className={cn(
 "w-10 h-5 rounded-full relative transition-all",
 form.isActive ? "bg-[#0176D3]" : "bg-[#D8DDE6]"
 )}>
              <div className={cn(
 "absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all shadow-sm",
 form.isActive ? "left-[22px]" : "left-0.5"
 )} />
            </div>
            <input type="checkbox" className="hidden" checked={form.isActive} onChange={e => set('isActive', e.target.checked)} />
          </label>
        </div>

        <div className="px-4 sm:px-6 py-3 border-t border-[#E5E5E5] flex justify-end gap-2 bg-white">
          <button className="h-9 px-4 bg-white border border-[#C9C9C9] text-[#0176D3] text-[13px] font-semibold rounded hover:bg-[#F3F2F2] transition-colors" onClick={onClose}>Cancel</button>
          <button 
            className="h-9 px-4 bg-[#0176D3] hover:bg-[#014486] text-white text-[13px] font-semibold rounded flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
            onClick={handleSave} 
            disabled={saving}
          >
            {saving ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
            Save
          </button>
        </div>
      </Card>
    </div>
  );
}

export default function SuperAdminPlans() {
  const [plans, setPlans] = useState([]);
  const [catalogGroups, setCatalogGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null); // null | 'create' | plan_object

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get('/admin/plans');
      setPlans(res.data.plans || []);
    } catch (e) {
      alertService.error('Connection Error', e.response?.data?.error || 'Failed to load plan registry.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Fetched once — the grouped feature picker inside PlanModal renders
  // straight off this rather than any hardcoded list (see FALLBACK_FEATURES
  // for what's used if this fetch hasn't resolved yet or fails).
  useEffect(() => {
    api.get('/admin/feature-catalog')
      .then(res => setCatalogGroups(res.data.groups || {}))
      .catch(() => {});
  }, []);

  // Plans are never permanently deleted — a historical invoice can still
  // reference this plan, and deleting it would leave that reference
  // dangling. This deactivates it instead: hidden from new signups/upgrades,
  // but every past invoice/company reference stays intact.
  const handleDelete = async (id, name) => {
    const isConfirmed = await alertService.confirm(
      'Deactivate Tier?',
      `Deactivate the "${name}" plan? It will no longer be offered to new/upgrading tenants, but existing invoices and any company still on it are unaffected. Plans are never permanently deleted.`,
      'danger',
      'Deactivate'
    );

    if (!isConfirmed) return;

    try {
      await api.delete(`/admin/plans/${id}`);
      alertService.toast('success', 'Plan deactivated');
      load();
    } catch (e) {
      alertService.error('Operation Failed', e.response?.data?.error || 'Failed to deactivate plan.');
    }
  };

  const handleSetDefaultTrial = async (id, name) => {
    const isConfirmed = await alertService.confirm(
      'Set Default Trial Plan?',
      `Every new signup will automatically be placed on "${name}" for its configured trial duration — no payment required. Any previously-set default trial plan will be unset.`,
      'warning',
      'Set Default'
    );
    if (!isConfirmed) return;
    try {
      await api.put(`/admin/plans/${id}/set-default-trial`);
      alertService.toast('success', 'Default trial plan updated — new signups will now start here.');
      load();
    } catch (e) {
      alertService.error('Operation Failed', e.response?.data?.error || 'Failed to set default trial plan.');
    }
  };

  const handleUnsetDefaultTrial = async (id) => {
    try {
      await api.put(`/admin/plans/${id}/unset-default-trial`);
      alertService.toast('success', 'Default trial plan cleared — new signups will no longer auto-receive a plan.');
      load();
    } catch (e) {
      alertService.error('Operation Failed', e.response?.data?.error || 'Failed to clear default trial plan.');
    }
  };

  const totalRevenue = plans.reduce((s, p) => s + (p.monthlyRevenue || 0), 0);
  const totalSubs = plans.reduce((s, p) => s + (p.subscriberCount || 0), 0);

  if (loading && plans.length === 0) return (
    <div className="w-full py-40 flex flex-col items-center justify-center gap-8 opacity-30">
       <Loader size={48} className="animate-spin text-[#0176D3]" />
       <div className="text-[11px] font-bold uppercase text-[#706E6B] animate-pulse">Syncing Plan Registry…</div>
    </div>
  );

  return (
    <div className="w-full pb-10 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <PageHeader title="Subscription Plans" icon={CreditCard} subtitle={`Define pricing, limits, and module entitlements for all clients. Active tiers: ${plans.filter(p => p.isActive).length}`} />

      {/* Analytics Matrix */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Card className="p-6 bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
           <div className="space-y-4">
              <div className="flex items-center justify-between">
                 <div className="w-10 h-10 rounded-md bg-[#F4F6F9] text-[#0176D3] border border-[#D8DDE6] flex items-center justify-center">
                    <TrendingUp size={20} />
                 </div>
                 <Badge variant="emerald" className="text-[11px] font-semibold">Revenue Analytics</Badge>
              </div>
              <div className="space-y-1">
                 <p className="text-[12px] text-[#706E6B]">Estimated Monthly Revenue</p>
                 <div className="text-2xl font-bold text-[#080707] tabular-nums">{formatCurrency(totalRevenue, PLATFORM_CURRENCY)}</div>
                 <p className="text-[12px] text-[#706E6B]">Aggregated from current active subscription nodes</p>
              </div>
           </div>
        </Card>
        <Card className="p-6 bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
           <div className="space-y-4">
              <div className="flex items-center justify-between">
                 <div className="w-10 h-10 rounded-md bg-[#F4F6F9] text-[#0176D3] border border-[#D8DDE6] flex items-center justify-center">
                    <Users size={20} />
                 </div>
                 <Badge variant="blue" className="text-[11px] font-semibold">User Allocation</Badge>
              </div>
              <div className="space-y-1">
                 <p className="text-[12px] text-[#706E6B]">Total Active Subscribers</p>
                 <div className="text-2xl font-bold text-[#080707] tabular-nums">{totalSubs} <span className="text-[14px] text-[#706E6B] font-normal">Units</span></div>
                 <p className="text-[12px] text-[#706E6B]">Distributed across {plans.length} operational tiers</p>
              </div>
           </div>
        </Card>
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
         <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-[#E5E5E5] text-[12px] font-semibold text-[#080707] shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
               <Activity size={14} className="text-[#0176D3]" /> Platform Sync: Online
            </div>
         </div>
         <button 
           className="h-9 px-4 bg-[#0176D3] hover:bg-[#014486] text-white text-[13px] font-semibold rounded whitespace-nowrap flex items-center gap-2 transition-colors"
           onClick={() => setModal('create')} 
         >
           <Plus size={16} /> Create New Plan
         </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {plans.map(plan => (
          <Card key={plan._id} padding="p-0" className={cn(
 "flex flex-col overflow-hidden relative group transition-all duration-300 border-[#D8DDE6] bg-white rounded-md shadow-sm",
 !plan.isActive && "opacity-60"
 )}>
             
            <div className="absolute top-4 right-4 z-20 flex flex-col gap-1.5 items-end">
              {!plan.isActive && <Badge variant="rose" className="text-[11px] font-semibold">Inactive</Badge>}
              {plan.planType === 'trial' && <Badge variant="blue" className="text-[11px] font-semibold">Trial</Badge>}
              {plan.isDefaultTrial && <Badge variant="success" className="text-[11px] font-semibold">Default for New Signups</Badge>}
            </div>

            <div className="p-6 flex-1 flex flex-col gap-6">
               <div className="space-y-2">
                  <h3 className="text-[18px] font-bold text-[#080707] truncate pr-16">{plan.name}</h3>
                  <div className="flex items-baseline gap-1">
                     {plan.planType === 'trial' ? (
                       <>
                         <span className="text-2xl font-bold text-[#080707] tabular-nums leading-none">Free</span>
                         <span className="text-[13px] text-[#706E6B]">for {plan.trialDurationDays || '—'} days</span>
                       </>
                     ) : (
                       <>
                         <span className="text-2xl font-bold text-[#080707] tabular-nums leading-none">{formatCurrency(plan.price, PLATFORM_CURRENCY)}</span>
                         <span className="text-[13px] text-[#706E6B]">/ {plan.billingCycle}</span>
                       </>
                     )}
                  </div>
                  {plan.description && (
                    <p className="text-[13px] text-[#706E6B] line-clamp-3">{plan.description}</p>
                  )}
               </div>

               <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="p-3 rounded-md bg-[#F4F6F9] border border-[#D8DDE6] text-center">
                     <div className="text-[16px] font-bold text-[#080707] mb-1">{plan.subscriberCount || 0}</div>
                     <div className="text-[11px] text-[#706E6B]">Accounts</div>
                  </div>
                  <div className="p-3 rounded-md bg-[#F4F6F9] border border-[#D8DDE6] text-center">
                     <div className="text-[16px] font-bold text-[#080707] mb-1">{formatCurrency(plan.monthlyRevenue || 0, PLATFORM_CURRENCY, { compact: true })}</div>
                     <div className="text-[11px] text-[#706E6B]">Revenue</div>
                  </div>
                  <div className="p-3 rounded-md bg-[#F4F6F9] border border-[#D8DDE6] text-center">
                     <div className="text-[16px] font-bold text-[#080707] mb-1">{plan.maxDevices}</div>
                     <div className="text-[11px] text-[#706E6B]">Devices</div>
                  </div>
               </div>

               <div className="space-y-4">
                  <div className="flex items-center justify-between text-[12px] font-semibold text-[#444444] border-b border-[#D8DDE6] pb-3">
                     <div className="flex items-center gap-2"><PieChart size={14} className="text-[#0176D3]" /> Entitlements</div>
                     <span className="text-[#0176D3] bg-[#E0F2FE] px-2 py-0.5 rounded-md border border-[#BAE6FD] text-[11px]">{plan.features?.length || 0} Active</span>
                  </div>
                  <PlanFeatureGroups groups={groupKeysByCatalog(plan.features || [], catalogGroups)} compact />
                  <div className="text-[12px] font-semibold text-[#444444] flex items-center justify-between pt-3 border-t border-[#D8DDE6]">
                     <div className="flex items-center gap-2"><Zap size={14} className="text-[#0176D3]" /> Message Limit:</div>
                     <span className="text-[#080707]">{plan.monthlyMessageLimit >= 999999 ? 'Unlimited' : plan.monthlyMessageLimit?.toLocaleString()}</span>
                  </div>
               </div>
            </div>

            {plan.planType === 'trial' && plan.isActive && (
              <div className="px-4 pt-3 bg-[#F4F6F9]">
                {plan.isDefaultTrial ? (
                  <button
                    className="w-full h-9 bg-white border border-[#E5E5E5] text-[#706E6B] text-[12px] font-bold rounded-lg hover:bg-[#F3F2F2] transition-colors shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                    onClick={() => handleUnsetDefaultTrial(plan._id)}
                  >
                    Clear as Default Trial
                  </button>
                ) : (
                  <button
                    className="w-full h-9 bg-[#04844B]/10 border border-[#04844B]/30 text-[#04844B] text-[12px] font-bold rounded-md hover:bg-[#04844B]/20 transition-colors"
                    onClick={() => handleSetDefaultTrial(plan._id, plan.name)}
                  >
                    Set as Default Trial for New Signups
                  </button>
                )}
              </div>
            )}

            <div className="p-4 border-t border-[#D8DDE6] flex gap-3 bg-[#F4F6F9] mt-auto">
               <button
                 className="h-9 px-4 bg-white border border-[#C9C9C9] text-[#0176D3] text-[13px] font-semibold rounded flex items-center justify-center gap-2 hover:bg-[#F3F2F2] transition-colors"
                 onClick={() => setModal(plan)}
               >
                  <Edit2 size={14} /> Manage Parameters
               </button>
               <button
                 className="w-9 h-9 rounded-lg bg-white border border-[#E5E5E5] flex items-center justify-center text-[#706E6B] hover:text-[#BA0517] hover:bg-[#FEF1EE] hover:border-[#FDDDE3] transition-colors shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                 title="Delete" aria-label="Delete"
                 onClick={() => handleDelete(plan._id, plan.name)}
               >
                 <Trash2 size={16} />
               </button>
            </div>
          </Card>
        ))}

        <button 
          onClick={() => setModal('create')} 
          className="rounded-md border-2 border-dashed border-[#D8DDE6] bg-white hover:border-[#0176D3] hover:bg-[#F4F6F9] transition-all group flex flex-col items-center justify-center py-12 gap-4 min-h-[300px]"
        >
           <div className="w-12 h-12 rounded-md bg-[#F4F6F9] border border-[#D8DDE6] flex items-center justify-center text-[#706E6B] group-hover:text-[#0176D3] group-hover:border-[#0176D3] transition-colors shadow-sm">
              <Plus size={24} strokeWidth={2} />
           </div>
           <div className="text-center space-y-1">
              <h3 className="text-[16px] font-bold text-[#080707] group-hover:text-[#0176D3] transition-colors">Add New Tier</h3>
              <p className="text-[13px] text-[#706E6B]">Expand platform business offerings</p>
           </div>
        </button>
      </div>

      {modal && (
        <PlanModal
          plan={modal === 'create' ? null : modal}
          catalogGroups={catalogGroups}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}
